import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Checkbox, Select, Modal, message, Radio } from 'antd';
import { UpOutlined, DownOutlined } from '@ant-design/icons';
import styles from './index.module.scss';
import { useAppSelector } from '@/store/hooks';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import icon2 from '@/assets/images/路径 1751@2x.png';
import { getVipPrice, saveUserVip } from '@/api/emergencyServiceApi';
import { saveEmergencyServiceOrder, saveDriverCancellationCarpoolOrder } from '@/api/car';
import { stripePay, boaPay } from '@/api/order';
import { useTranslation } from 'react-i18next';
const { Option } = Select;

interface MembershipChildProps {
  open?: boolean;
  onCancel?: () => void;
  onConfirm?: (values: any) => void;
  membershipType?: number; // 0: Emergency Service, 1: Ride Board Member, 2: VIP Member
  isCarpoolService?: boolean; //用户拼车服务竞价付款，在yourtownloop/postorders页面使用
  carpoolBiddingPrice?: number; //用户拼车服务竞价付款，在yourtownloop/postorders页面使用
  isDriverCancellation?: boolean; //司机有偿取消订单
  carpoolOrderId?: string | number; //拼车订单ID，用于司机有偿取消和拼车服务付款
  carpoolBiddingId?: number; //竞价记录ID，用于自动接受竞价
}

const MembershipChild: React.FC<MembershipChildProps> = ({
  open = false,
  onCancel = () => { },
  onConfirm = () => { },
  membershipType,
  isCarpoolService = false,
  carpoolBiddingPrice = 0, //用户拼车服务竞价付款，在yourtownloop/postorders页面使用
  isDriverCancellation = false, //司机有偿取消订单
  carpoolOrderId, //拼车订单ID，用于司机有偿取消
  carpoolBiddingId, //竞价记录ID
}) => {
  const [form] = Form.useForm();
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [selectedDuration, setSelectedDuration] = useState('1year');
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [cardComplete, setCardComplete] = useState(false);
  const [vipPrices, setVipPrices] = useState<any[]>([]);
  const [selectedMiles, setSelectedMiles] = useState<number | null>(null);
  const [filteredVipPrices, setFilteredVipPrices] = useState<any[]>([]);
  const {t} = useTranslation()

  // 美国州列表
  const StateList = useAppSelector((state) => state.common.StateList);

  useEffect(() => {
    if (!open) return; // 弹窗未打开时不调用接口

    const fetchVipPrices = async () => {
      try {
        const type = typeof membershipType === 'number' ? membershipType : 0;
        const response = await getVipPrice({ membership_type: type });
        if (response && response.code === 200) {
          const data = response.data || [];
          setVipPrices(data);

          // 获取所有可用的miles选项（从extension数组中提取）
          const allMiles = data
            .map((item: any) => item.extension)
            .filter(Boolean)
            .flat()
            .map((ext: any) => ext.mile)
            .filter(Boolean);
          const uniqueMiles = Array.from(new Set(allMiles));
          if (uniqueMiles.length > 0) {
            setSelectedMiles(uniqueMiles[0] as number);
          }

          // 根据period_type设置默认时长
          if (data && data.length > 0) {
            const firstVip = data[0];
            if (firstVip.period_type === 1) {
              setSelectedDuration('1month');
            } else if (firstVip.period_type === 2) {
              setSelectedDuration('1quarter');
            } else if (firstVip.period_type === 3) {
              setSelectedDuration('1year');
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch VIP prices:', error);
      }
    };

    fetchVipPrices();
  }, [open, membershipType]);

  // 根据选择的miles筛选vipPrices
  useEffect(() => {
    if (selectedMiles !== null) {
      const filtered = vipPrices.filter((item: any) =>
        item.extension &&
        item.extension.some((ext: any) => ext.mile === selectedMiles)
      );
      setFilteredVipPrices(filtered);
    } else {
      setFilteredVipPrices(vipPrices);
    }
  }, [selectedMiles, vipPrices]);



  // 从API获取时长（天数）
  const getDurationFromApi = () => {
    if (filteredVipPrices && filteredVipPrices.length > 0) {
      const vipInfo = filteredVipPrices.find(item => {
        if (selectedDuration === '1month' && item.period_type === 1) return true;
        if (selectedDuration === '1quarter' && item.period_type === 2) return true;
        if (selectedDuration === '1year' && item.period_type === 3) return true;
        return false;
      });
      if (vipInfo && typeof vipInfo.duration_days === 'number') {
        return vipInfo.duration_days;
      }
    }
  };

  // 根据类型动态显示
  const getMembershipInfo = () => {
    switch (membershipType) {
      case 0:
        return {
          title: '🚨 Emergency Service',
          description: 'Helping You Solve Everyday Service Needs',
          checkbox: 'Agree To Emergency Service Agreement'
        };

      case 1:
        return {
          title: t('membershipChild.RideMember'),
          description: t('membershipChild.Suitableplay'),
          checkbox: t('membershipChild.AgreeAgreement')
        };
      default:
        return {
        };
    }
  };

  // 从接口价格表中取价格（按 period_type 匹配 1month/1quarter/1year）
  const getPriceFromApi = () => {
    if (filteredVipPrices && filteredVipPrices.length > 0) {
      const matched = filteredVipPrices.find((item: any) => {
        if (selectedDuration === '1month') return item.period_type === 1;
        if (selectedDuration === '1quarter') return item.period_type === 2;
        if (selectedDuration === '1year') return item.period_type === 3;
        return false;
      });
      if (matched && matched.price != null) {
        const price = Number(matched.price);
        return Number.isFinite(price) ? price : 0;
      }
    }
    return 0;
  };

  // 对外统一使用此方法获取价格
  const getPrice = () => getPriceFromApi();

  // 计算到期日期（基于API返回的duration_days）
  const getExpiryDate = () => {
    const now = new Date();
    const durationDays = getDurationFromApi();

    // 计算到期日期：当前日期 + duration_days
    const expiryDate = new Date(now.getTime() + (durationDays * 24 * 60 * 60 * 1000));

    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'America/New_York'
    };

    return expiryDate.toLocaleDateString('en-US', options);
  };


  const membershipInfo = getMembershipInfo();
  //当前价格
  const currentPrice = isDriverCancellation 
    ? Number((carpoolBiddingPrice * 0.05).toFixed(2)) //司机有偿取消订单价格
    : isCarpoolService 
    ? Number(carpoolBiddingPrice) //用户拼车服务竞价付款价格
    : getPrice(); //会员价格
  const expiryDate = getExpiryDate();

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  // 处理信用卡信息变更
  const handleCardChange = (event: any) => {
    setCardComplete(event.complete);
  };

  // 支付会员
  const handleSubmit = async () => {
    try {
      //debugger
      const values = await form.validateFields();

      // 检查是否同意服务协议
      if (!values.agreement) {
        message.error('Please agree to this service agreement before proceeding');
        return;
      }

      // 如果选择Stripe支付方式，检查信用卡信息是否完整
      if (paymentMethod === 'stripe' && !cardComplete) {
        message.error('Please complete all credit card details before proceeding');
        return;
      }

      setLoading(true);

      // 添加会员类型信息到提交数据中
      const submitData = {
        payment_method: paymentMethod,
        price: currentPrice,
        vip_value: 0,
        membership_type: membershipType,
        ...(selectedMiles !== null && {
          extension: [
            {
              mile: selectedMiles
            }
          ]
        }),
        billing_address: {
          email: values.email,
          recipient_phone: values.recipient_phone,
          address: values.address,
          zip_code: values.zip_code,
          city: values.city,
          state: values.state,
          first_name: values.first_name,
          last_name: values.last_name,
          building: values.building,
          // extension: values.extension
        }
      };
      const foundItem = vipPrices.find(item => {
        const itemPrice = Number(item.price).toFixed(2);
        const submitPrice = submitData.price.toFixed(2);
        if(submitPrice === itemPrice){
          localStorage.setItem('period_type',item.period_type)
        }
        return submitPrice === itemPrice;
      });

      if (foundItem) {
        submitData.vip_value = foundItem.vip_value;
      }
      console.log(submitData, foundItem, vipPrices, "submitData");
      try {
        // 先调用saveUserVip接口
        const vipRes = await saveUserVip(submitData);
        if (vipRes && vipRes.code === 200) {
          console.log(vipRes, "vipRes.data");

          // 解析返回的数据字符串
          let vipData;
          try {
            if (typeof vipRes.data === 'string') {
              // 处理单引号的问题，将单引号替换为双引号
              const jsonString = vipRes.data.replace(/'/g, '"');
              console.log("vipRes.data is a string")
              vipData = JSON.parse(jsonString);
            } else {
              console.log("vipRes.data is not a string")
              vipData = vipRes.data;
            }
          } catch (parseError) {
            console.error('Failed to parse vipRes.data:', parseError);
            message.error('Failed to process VIP data');
            return;
          }

          // saveUserVip成功后，根据支付方式调用相应的支付接口
          if (paymentMethod === 'bank') {
            // 美国银行支付
            await handleBoaPay(values, vipData);
          } else if (paymentMethod === 'stripe') {
            // Stripe支付
            await handleStripePay(values, vipData);
          }
        } else {
          message.error(vipRes?.msg || 'Failed to save VIP membership');
        }
      } catch (error) {
        console.error('Error during VIP processing:', error);
        message.error('An error occurred during the process');
      } finally {
        setLoading(false);
      }
    } catch (error) {
      console.error('Form validation failed:', error);
      setLoading(false);
    }
  };

  // 支付拼车订单
  const handleCarpoolPay = async () => {
    try {
      //debugger
      const values = await form.validateFields();

      // 如果选择Stripe支付方式，检查信用卡信息是否完整
      if (paymentMethod === 'stripe' && !cardComplete) {
        message.error('Please complete all credit card details before proceeding');
        return;
      }

      setLoading(true);

      // 添加会员类型信息到提交数据中
      let submitData: any = {
        payment_method: paymentMethod,
        price: currentPrice,
        billing_address: {
          email: values.email,
          recipient_phone: values.recipient_phone,
          address: values.address,
          zip_code: values.zip_code,
          city: values.city,
          state: values.state,
          first_name: values.first_name,
          last_name: values.last_name,
          building: values.building,
          // extension: values.extension
        }
      };

      let api = saveEmergencyServiceOrder;

      // ✅ 如果是拼车服务付款，添加拼车订单ID和竞价ID
      if (isCarpoolService && carpoolOrderId) {
        submitData.carpool_order_id = carpoolOrderId;
        if (carpoolBiddingId) {
          submitData.carpool_bidding_id = carpoolBiddingId;  // 传递竞价记录ID
        }
      }

      // 如果是司机有偿取消，添加订单ID
      if (isDriverCancellation && carpoolOrderId) {
        submitData = {
          order_id: carpoolOrderId,
          responsible_fee: currentPrice,
        };

        api = saveDriverCancellationCarpoolOrder;
      }



      try {
        // 先调用saveEmergencyServiceOrder接口
        const vipRes = await api(submitData);
        if (vipRes && vipRes.code === 200) {
          console.log(vipRes, "vipRes.data");

          // 解析返回的数据字符串
          let vipData;
          try {
            if (typeof vipRes.data === 'string') {
              // 处理单引号的问题，将单引号替换为双引号
              const jsonString = vipRes.data.replace(/'/g, '"');
              vipData = JSON.parse(jsonString);
            } else {
              vipData = vipRes.data;
            }
          } catch (parseError) {
            console.error('Failed to parse vipRes.data:', parseError);
            message.error('Failed to process VIP data');
            return;
          }

          // saveUserVip成功后，根据支付方式调用相应的支付接口
          if (paymentMethod === 'bank') {
            // 美国银行支付
            await handleBoaPay(values, vipData);
          } else if (paymentMethod === 'stripe') {
            // Stripe支付
            await handleStripePay(values, vipData);
          }
        } else {
          message.error(vipRes?.msg || 'Failed to save VIP membership');
        }
      } catch (error) {
        console.error('Error during VIP processing:', error);
        message.error('An error occurred during the process');
      } finally {
        setLoading(false);
      }
    } catch (error) {
      console.error('Form validation failed:', error);
      setLoading(false);
    }
  };

  

  // 美国银行支付处理
  const handleBoaPay = async (formValues: any, vipData: any) => {
    const bankParams = {
      amount: Number(currentPrice),
      bill_to_address_city: formValues.city || "",
      bill_to_address_country: "US",
      bill_to_address_line1: formValues.address || "",
      bill_to_address_line2: "",
      bill_to_address_postal_code: formValues.zip_code || "",
      bill_to_address_state: formValues.state || "",
      bill_to_company_name: "",
      bill_to_email: formValues.email || "",
      bill_to_forename: formValues.first_name || "",
      bill_to_phone: formValues.recipient_phone || "",
      bill_to_surname: formValues.last_name || "",
      order_id: vipData.order_id,
    };

    try {
      const res = await boaPay(bankParams);
      if (res.code === 200) {
        const boaData = res.data;

        // 若选择了英里数，则把临时英里数写入 localStorage 以便页面即时展示
        if (typeof selectedMiles === 'number' && Number.isFinite(selectedMiles)) {
          try {
            const existed = Number(localStorage.getItem('vipTempMiles') || '0');
            if (!Number.isFinite(existed) || selectedMiles > existed) {
              localStorage.setItem('vipTempMiles', String(selectedMiles));
            }
          } catch { }
        }

        // 调用父组件的确认回调，传递支付成功信息
        onConfirm({
          paymentSuccess: true,
          paymentMethod: 'bank',
          orderId: vipData.order_id,
          ...(isDriverCancellation && carpoolOrderId && { carpoolOrderId: carpoolOrderId }),
        });

        const forms = document.createElement("form");
        forms.action = process.env.REACT_APP_BOA_PAY_URL || "";
        forms.method = "POST";
        forms.style.display = "none";

        for (let key in boaData) {
          let input = document.createElement("input");
          input.type = "hidden";
          input.name = key;
          input.value = boaData[key];
          forms.appendChild(input);
        }

        document.body.appendChild(forms);
        forms.submit();
        document.body.removeChild(forms);
      } else {
        message.error(res.msg || 'Bank payment failed');
      }
    } catch (error) {
      console.error('Bank payment error:', error);
      message.error('Bank payment error occurred');
    }
  };

  // Stripe支付处理
  const handleStripePay = async (formValues: any, vipData: any) => {
    //debugger
    if (!stripe || !elements) {
      message.error('Stripe is not initialized');
      return;
    }

    // 创建一个 AbortController 实例，可以取消请求
    const controller = new AbortController();
    const signal = controller.signal;

    const stripeParams: any = {
      order_total_price: Number(currentPrice),
      user_id: vipData.user_id,
      order_id: vipData.order_id,
      email: formValues.email,
      phone: formValues.recipient_phone,
      name: formValues.first_name + " " + formValues.last_name,
      addr: formValues.address,
      payment_type: "stripe",
      signal: signal,
    };
    
    // 如果有竞价ID，传递给支付接口
    if (vipData.carpool_bidding_id) {
      stripeParams.carpool_bidding_id = vipData.carpool_bidding_id;
    }

    try {
      const res = await stripePay(stripeParams);

      if (res && res.code === 200) {
        const clientSecret = res.data.clientSecret;

        // 支付调用
        const result = await stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: elements.getElement(CardElement)!,
            billing_details: {
              name: stripeParams.name,
              email: stripeParams.email,
              phone: stripeParams.phone,
              address: {
                line1: stripeParams.addr,
              },
            },
          },
        });

        if (result.error) {
          message.error(result.error.message || 'Payment error');
        } else if (result.paymentIntent?.status === 'succeeded') {
          message.success('Payment successful');
          // 关闭弹窗
          onCancel();
          // 若选择了英里数，则把临时英里数写入 localStorage 以便页面即时展示
          if (typeof selectedMiles === 'number' && Number.isFinite(selectedMiles)) {
            try {
              const existed = Number(localStorage.getItem('vipTempMiles') || '0');
              if (!Number.isFinite(existed) || selectedMiles > existed) {
                localStorage.setItem('vipTempMiles', String(selectedMiles));
              }
            } catch { }
          }
          // 调用父组件的确认回调，传递支付成功信息
          onConfirm({
            paymentSuccess: true,
            paymentMethod: 'stripe',
            orderId: vipData.order_id,
            ...(isDriverCancellation && carpoolOrderId && { carpoolOrderId: carpoolOrderId }),
            ...( vipData.payment_intent_id && { payment_intent_id: vipData.payment_intent_id } ),
          });
        }
      } else {
        message.error(res?.msg || 'Stripe payment failed');
      }
    } catch (error) {
      console.error('Stripe payment error:', error);
      message.error('Stripe payment error occurred');
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      width={1000}
      centered
      className={styles.membershipModal}
      styles={{
        content: {
          backgroundColor: '#eeeeee',
          padding: '20px 40px',
        }
      }}
    >
      <div className={styles.modalContent}>
        {/*  Billing Address 填写表单*/}
        <div className={styles.billingSection} style={{ backgroundColor: '#ffffff', padding: '24px' }}>
          <h3 className={styles.sectionTitle}>{t('membershipChild.Billingress')}</h3>
          <Form form={form} layout="vertical" requiredMark>
            <Form.Item
              label={<><span>*</span> {t('membershipChild.FirstName')}</>}
              name="first_name"
              rules={[{ required: true, message: t('storeEntry.Pleaseenterfirstname') }]}
            >
              <Input placeholder={t('membershipChild.FirstName')} />
            </Form.Item>

            <Form.Item
              label={<><span>*</span> {t('membershipChild.LastName')}</>}
              name="last_name"
              rules={[{ required: true, message: t('storeEntry.Pleaseenterlastname') }]}
            >
              <Input placeholder={t('membershipChild.LastName')} />
            </Form.Item>

            <Form.Item
              label={<><span>*</span> {t('membershipChild.Email')}</>}
              name="email"
              rules={[{ required: true, message: t('storeEntry.PleaseenterEmail') }]}
            >
              <Input placeholder={t('membershipChild.Email')} type="email" />
            </Form.Item>

            <Form.Item
              label={<><span>*</span> {t('membershipChild.Phone')}</>}
              name="recipient_phone"
              rules={[{ required: true, message: t('storeEntry.Pleaseenterphone') }]}
            >
              <Input placeholder={t('membershipChild.Phone')} />
            </Form.Item>

            {/* <Form.Item 
              label={<><span>*</span> Extension</>}
              name="extension" 
              rules={[{ required: true, message: 'Please enter extension' }]}
            >
              <Input placeholder="Extension" />
            </Form.Item> */}

            <Form.Item
              label={<><span>*</span> {t('membershipChild.State')}</>}
              name="state"
              rules={[{ required: true, message: t('storeEntry.Pleaseselectstate') }]}
            >
              <Select
                placeholder={t('membershipChild.CityState')}
                allowClear
              >
                {StateList.map((item, index) => (
                  <Option key={index} value={item.value}>{item.label}</Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label={<><span>*</span> {t('membershipChild.City')}</>}
              name="city"
              rules={[{ required: true, message: t('storeEntry.Pleaseentercity') }]}
            >
              <Input placeholder={t('membershipChild.City')} />
            </Form.Item>

            <Form.Item
              label={<><span>*</span> {t('membershipChild.Address')}</>}
              name="address"
              rules={[{ required: true, message: t('storeEntry.Pleaseenteraddress') }]}
            >
              <Input placeholder={t('membershipChild.Address')} />
            </Form.Item>

            <Form.Item
              label={<><span>*</span> {t('membershipChild.AptBuilding')}</>}
              name="building"
              rules={[{ required: true, message: t('storeEntry.Pleaseenterbuilding') }]}
            >
              <Input placeholder={t('membershipChild.Building')} />
            </Form.Item>

            <Form.Item
              label={<><span>*</span> {t('membershipChild.ZipCode')}</>}
              name="zip_code"
              rules={[{ required: true, message: t('storeEntry.Pleaseenterzipcode') }]}
            >
              <Input placeholder={t('membershipChild.ZipCode')} />
            </Form.Item>
          </Form>
        </div>

        {/* Right Section: Membership Selection */}
        <div className={styles.membershipSection} style={{ backgroundColor: '#ffffff', padding: '24px' }}>
          {
            isDriverCancellation ? (
              // 司机有偿取消订单
              <>
                <h3 className={styles.sectionTitle}>Cancel The Order</h3>
                <p style={{ color: '#52c41a', fontSize: '14px', marginBottom: '20px' }}>
                  Canceling The Order In The Current Status Will <span style={{ fontWeight: 'bold' }}>Incur A 5% Fee Deduction.</span>
                </p>
              </>
            ) : !isCarpoolService ? (
              <h3 className={styles.sectionTitle}>{t('membershipChild.Chooseyou')}</h3>
            ) : (
              // 用户拼车服务竞价付款
              <h3 className={styles.sectionTitle}>Carpool Service Payment</h3>
            )
          }


          <Form form={form} layout="vertical">
            {
              (!isCarpoolService && !isDriverCancellation) && (
                <div className={styles.membershipOption}>
                  <div className={styles.optionHeader}>
                    <span className={styles.optionTitle}>{membershipInfo.title}</span>

                    <span className={styles.optionPrice}>${currentPrice}/{selectedDuration === '1month' ? t('membershipChild.month') : selectedDuration === '1quarter' ? t('membershipChild.quarter') : t('membershipChild.year')}</span>

                  </div>
                  <div className={styles.descriptionRow} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <span className={styles.optionDescription}>{membershipInfo.description}</span>
                    <div className={styles.retractSection} onClick={toggleExpand} style={{ cursor: 'pointer' }}>
                      <span>{isExpanded ? t('membershipChild.Retract') : t('membershipChild.ShowMore')}</span>
                      <span className={styles.retractIcon}>
                        {isExpanded ? <UpOutlined /> : <DownOutlined />}
                      </span>
                    </div>
                  </div>
                  <div className={styles.separatorLine} style={{ borderTop: '1px solid #e8e8e8', marginBottom: '15px' }}></div>
                  {isExpanded && (
                    <div className={styles.benefitsList}>
                      <div className={styles.benefitItem}>
                        <span ><img src={icon2} alt="icon2" /></span>

                        <span>{t('membershipChild.Moreconvenient')}</span>
                      </div>
                      <div className={styles.benefitItem}>
                        <span ><img src={icon2} alt="icon2" /></span>
                        <span>{t('membershipChild.Fasterefficient')}</span>
                      </div>
                      <div className={styles.benefitItem}>
                        <span ><img src={icon2} alt="icon2" /></span>
                        <span>{t('membershipChild.Morecomfortable')}</span>
                      </div>
                    </div>
                  )}
                </div>
              )
            }


            {/* Miles 只在membership_type=0时显示 */}
            {(!isCarpoolService &&!isDriverCancellation && membershipType === 0) && (
              <div className={styles.purchaseDuration}>
                <div className={styles.leftContent}>
                  <span>Search Range:</span>
                  <p className={styles.durationNote}>Select the search range for emergency services</p>
                </div>
                <Select
                  value={selectedMiles}
                  onChange={(value) => setSelectedMiles(value)}
                  className={styles.durationSelect}
                  style={{ width: '120px' }}
                  placeholder="Select Miles"
                >
                  {Array.from(new Set(
                    vipPrices
                      .filter((item: any) => item.extension && item.extension.length > 0)
                      .map((vip: any) => vip.extension)
                      .flat()
                      .map((ext: any) => ext.mile)
                      .filter(Boolean)
                  )).map((mile: number) => (
                    <Option key={`miles-${mile}`} value={mile}>
                      {mile} Miles
                    </Option>
                  ))}
                </Select>
              </div>
            )}

            {/* 会员时长选择 */}
            {
              (!isCarpoolService && !isDriverCancellation) && (
                <div className={styles.purchaseDuration}>
                  <div className={styles.leftContent}>
                    <span>{t('membership.PurchaseDuration')}:</span>
                    <p className={styles.durationNote}>{t('membershipChild.Canuntil')} {expiryDate}</p>
                  </div>
                  <Select
                    value={selectedDuration}
                    onChange={(value) => setSelectedDuration(value)}
                    className={styles.durationSelect}
                    style={{ width: '120px' }}
                  >
                    {filteredVipPrices.map((vip) => {
                      if (vip.period_type === 1) {
                        return <Option key={`1month-${vip.vip_value}`} value="1month">{t('membershipChild.month')}</Option>;
                      } else if (vip.period_type === 2) {
                        return <Option key={`1quarter-${vip.vip_value}`} value="1quarter">{t('membershipChild.quarter')}</Option>;
                      } else if (vip.period_type === 3) {
                        return <Option key={`1year-${vip.vip_value}`} value="1year">{t('membershipChild.year')}</Option>;
                      }
                      return null;
                    })}
                  </Select>

                </div>
              )
            }


            <div className={styles.paymentMethod}>
              <div>{t('membershipChild.Paymentmethod')}</div>
            </div>
            <div className={styles.orderPaymentMethod}>
              {/* 支付方式 */}
              <div>
                <Radio.Group
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                >
                  <Radio value="stripe">Stripe</Radio>
                  <Radio value="bank">Boa</Radio>
                </Radio.Group>
              </div>
              {/* stripe支付 */}
              {paymentMethod === 'stripe' && (
                <div style={{ margin: '20px 0' }}>
                  <CardElement
                    onChange={handleCardChange}
                    options={{
                      style: {
                        base: {
                          fontSize: '16px',
                          color: '#32325d',
                          '::placeholder': {
                            color: '#aab7c4',
                          },
                        },
                        invalid: {
                          color: '#fa755a',
                        },
                      },
                    }}
                  />
                  <div style={{ marginTop: '10px', color: cardComplete ? '#008000' : '#ff0000' }}>
                    {cardComplete ? t('membershipChild.CardComplete') : t('membershipChild.Pleasedetails')}
                  </div>
                </div>
              )}
            </div>

            {/* 会员费用详情 */}
            {
              (!isCarpoolService && !isDriverCancellation) && (
                <>
                  <div className={styles.purchaseDuration}>

                    {/* <div className={styles.leftContent}>
                      <span className={styles.costDetailsTitle}>{t('membershipChild.CostDetails')}</span>
                      <span className={styles.durationNote}>Membership for {selectedDuration === '1month' ? 'one month' : selectedDuration === '1quarter' ? 'one quarter' : 'one year'}</span>
                    </div> 
                    <div>
                      <span>${currentPrice}.00</span>
                    </div>*/}
                  </div>
                  <div className={styles.totalRow}>
                    <span>{t('membershipChild.Total')}</span>

                    <span>${currentPrice}.00</span>
                  </div>
                </>
              )
            }
            {/* 用户拼车服务竞价付款展示价格 */}
            {
              (isCarpoolService || isDriverCancellation) && (
                <>
                  {isDriverCancellation && (
                    <div className={styles.purchaseDuration}>
                      <div className={styles.leftContent}>
                        <span className={styles.costDetailsTitle}>Total product price</span>
                      </div>
                      <div>
                        <span>${carpoolBiddingPrice.toFixed(2)}</span>
                      </div>
                    </div>
                  )}
                  <div className={styles.totalRow}>
                    <span>{isDriverCancellation ? 'Payment Details' : 'Total'}:</span>
                    <span>${currentPrice.toFixed(2)}</span>
                  </div>
                </>
              )
            }    
            

            {
              (!isCarpoolService && !isDriverCancellation) ? (
                // 支付会员
                <Button
                  type="primary"
                  className={styles.confirmButton}
                  onClick={handleSubmit}
                  loading={loading}
                  disabled={loading || (paymentMethod === 'stripe' && !cardComplete)}
                  block
                >
                  {t('membershipChild.ConfirmPurchase')}
                </Button>
              ):(
                // 支付拼车订单或司机取消订单
                <Button
                  type="primary"
                  className={styles.confirmButton}
                  onClick={handleCarpoolPay}
                  loading={loading}
                  disabled={loading || (paymentMethod === 'stripe' && !cardComplete)}
                  block
                >
                  {isDriverCancellation ? 'Pay' : 'Pay Now'}
                </Button>
              )
            }


            {
              (!isCarpoolService && !isDriverCancellation) && (
                <Form.Item
                  name="agreement"
                  valuePropName="checked"
                >
                  <Checkbox>
                    {membershipInfo.checkbox}
                  </Checkbox>
                </Form.Item>
              )
            }

            
          </Form>
        </div>
      </div>
    </Modal>
  );
};

export default MembershipChild;
